import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { EmployeeForSelector } from '@/api/employees';

interface EmployeeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  employees: EmployeeForSelector[] | undefined;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function EmployeeSelector({
  value,
  onChange,
  employees,
  isLoading,
  placeholder = 'Выберите сотрудника',
  disabled,
}: EmployeeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedEmployee = employees?.find((emp) => emp.id === value);

  const filteredEmployees = useMemo(() => {
    if (!employees) return [];
    if (!search.trim()) return employees;

    const searchLower = search.toLowerCase();
    return employees.filter((emp) =>
      emp.displayName.toLowerCase().includes(searchLower)
    );
  }, [employees, search]);

  const handleSelect = (employeeId: string) => {
    onChange(employeeId);
    setOpen(false);
    setSearch('');
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          {selectedEmployee ? selectedEmployee.displayName : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) p-0 bg-background">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            placeholder="Поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {isLoading ? (
            <div className="p-2 text-center text-sm text-muted-foreground">
              Загрузка...
            </div>
          ) : filteredEmployees.length > 0 ? (
            filteredEmployees.map((emp) => (
              <div
                key={emp.id}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer',
                  'hover:bg-accent hover:text-accent-foreground',
                  value === emp.id && 'bg-accent'
                )}
                onClick={() => handleSelect(emp.id)}
              >
                <Check
                  className={cn(
                    'h-4 w-4',
                    value === emp.id ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {emp.displayName}
              </div>
            ))
          ) : (
            <div className="p-2 text-center text-sm text-muted-foreground">
              {search ? 'Ничего не найдено' : 'Нет сотрудников'}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
